import React, { useEffect, useState } from 'react';
import './InstagramCooldownNotice.css';

type Props = {
  /** ISO timestamp from `workspace.instagram_cooldown_until`. */
  until?: string | null;
  /** Compact variant for inline/onboarding contexts. */
  compact?: boolean;
};

/** Milliseconds left on the cooldown, or 0 once it has lapsed. */
export function cooldownRemainingMs(until?: string | null): number {
  if (!until) return 0;
  const ends = new Date(until).getTime();
  if (Number.isNaN(ends)) return 0;
  return Math.max(0, ends - Date.now());
}

/** True while Instagram writes are frozen for this workspace. */
export function isInCooldown(until?: string | null): boolean {
  return cooldownRemainingMs(until) > 0;
}

function humanize(ms: number): string {
  const minutes = Math.ceil(ms / 60000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) {
    if (rest === 0) return `${hours} hour${hours === 1 ? '' : 's'}`;
    return `${hours}h ${rest}m`;
  }
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Shown while the risk breaker has publishing paused.
 *
 * The whole point of this component is what it does *not* say. Instagram
 * rate-limiting an account and an expired session look similar from the
 * outside, but the remedies are opposite: a dead session needs a fresh login,
 * a soft block needs everybody to stop touching the account. The old UI showed
 * "reconnect below" for every unhealthy state, which during a block is the one
 * action that makes it worse. So this is reassuring and explicitly tells the
 * user not to reconnect.
 */
export function InstagramCooldownNotice({ until, compact = false }: Props) {
  const [remaining, setRemaining] = useState(() => cooldownRemainingMs(until));

  useEffect(() => {
    setRemaining(cooldownRemainingMs(until));
    if (!until) return undefined;
    // A minute is enough resolution for an hours-long pause, and keeps this
    // from being a once-per-second render for something nobody watches tick.
    const id = setInterval(() => setRemaining(cooldownRemainingMs(until)), 30000);
    return () => clearInterval(id);
  }, [until]);

  if (remaining <= 0) return null;

  return (
    <div className={`ig-cooldown${compact ? ' ig-cooldown--compact' : ''}`} role="status">
      <span className="ig-cooldown__icon" aria-hidden="true">
        ⏸
      </span>
      <div className="ig-cooldown__body">
        <strong className="ig-cooldown__title">
          Publishing paused for about {humanize(remaining)}
        </strong>
        <p className="ig-cooldown__text">
          Instagram asked us to slow down on this account, so we stopped posting
          for a while to protect it. Nothing is lost — scheduled posts go out
          automatically once the pause lifts.
        </p>
        <p className="ig-cooldown__text ig-cooldown__text--strong">
          Your login is still fine. Please don&apos;t reconnect or change the
          account while this is active — that makes it worse. Using Instagram
          normally from your phone helps it clear faster.
        </p>
      </div>
    </div>
  );
}
